import { Component, EventEmitter, inject, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { NgModel } from '@angular/forms';
import { ColorMetadataService } from '@ddv/charts';
import { DropdownOption } from '@ddv/common-components';
import { CellColors, CellFormat, ColumnDisplayType, FormatOperator, LabelValuePair, MetadataFormatUtils } from '@ddv/models';
import { dateFormat } from '@ddv/utils';
import { ThemeService } from '@hs/ui-core-presentation';
import { Subscription } from 'rxjs';

import { ColumnConditionVizTab, VizTab } from '../../../models/column-condition-viz-tab';
import { showInputRange } from '../../../utils/show-input-range';

@Component({
    selector: 'ddv-conditional-formatting',
    templateUrl: './conditional-formatting.component.html',
    styleUrls: ['./conditional-formatting.component.scss'],
    providers: [NgModel],
    standalone: false,
})
export class ConditionalFormattingComponent implements OnInit, OnChanges, OnDestroy {
    @Input() displayType: ColumnDisplayType = 'string';
    @Input() columnConditions: CellFormat[] = [];
    @Input() valueOperators: DropdownOption[] | undefined;
    @Output() columnConditionChange = new EventEmitter<CellFormat[]>();

    private readonly themeService = inject(ThemeService);

    protected columnConditionVizTab: ColumnConditionVizTab[] = [{ isViz: true, vizTabs: [], isColumnGrid: true }];
    protected acceptedDateFormat = dateFormat;
    protected dateRegex = '^\\d{0,2}[\\/]{0,1}\\d{0,2}[\\/]{0,1}\\d{0,4}$';
    protected numberRegex = '^-?\\d*(\\.\\d*)?$';
    protected barPercentRegex = '^(\\d{1,2}(\\.\\d*)?|100)$';
    protected barColors: LabelValuePair<string>[] = [];
    protected cellAndFontColors: CellColors[] = [];
    protected fuzzyDates: DropdownOption[] = [];

    private themeSubscription: Subscription | undefined;

    ngOnInit(): void {
        this.themeSubscription = this.themeService.currentTheme$.subscribe((theme) => {
            this.cellAndFontColors = ColorMetadataService.getCellAndFontColors(theme);
            this.barColors = ColorMetadataService.getColors(theme, true);
            this.fuzzyDates = MetadataFormatUtils.getDateOccurringValues().map((d) => {
                return { text: d.label, key: d.value, value: d.value, css: d.value };
            });

            if (this.columnConditions.length) {
                this.columnConditions.forEach((columnCondition, index) => {
                    const guid = this.columnConditionVizTab[0].vizTabs.length;
                    if (index === this.columnConditions.length - 1) {
                        this.addColumnConditionVizTab(guid, true, columnCondition);
                    } else {
                        this.addColumnConditionVizTab(guid, false, columnCondition);
                    }
                });
            }
        });
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.displayType) {
            if (!changes.displayType.firstChange) {
                this.columnConditionVizTab[0].vizTabs.splice(0, this.columnConditionVizTab[0].vizTabs.length);
                this.columnConditions.splice(0, this.columnConditions.length);

                if (changes.displayType.currentValue === 'bar') {
                    this.addConditionalFormatters();
                }
            }
        }
    }

    ngOnDestroy(): void {
        this.themeSubscription?.unsubscribe();
    }

    protected removeConditionalFormatters(tab: VizTab): void {
        let activeTabGuid = this.columnConditionVizTab[0].vizTabs.length - 1;
        if (tab.active) {
            if (tab.guid !== 0) {
                activeTabGuid = tab.guid - 1;
            } else if (tab.guid !== activeTabGuid) {
                activeTabGuid = tab.guid;
            }
        }

        this.columnConditions.splice(tab.guid, 1);
        this.columnConditionVizTab = this.columnConditionVizTab.map((ccVizTab) => {
            return {
                ...ccVizTab,
                vizTabs: ccVizTab.vizTabs.map((vizTab, i) => {
                    return { ...vizTab, guid: i, label: `CN ${i + 1}`, active: i === activeTabGuid };
                }),
            };
        });
        this.columnConditionChange.emit(this.columnConditions);
    }

    protected addConditionalFormatters(): void {
        this.columnConditionVizTab[0].vizTabs.forEach((tab) => tab.active = false);
        const columnCondition = {
            fontColor: this.cellAndFontColors[0].fontColor,
            cellColor: this.cellAndFontColors[0].cellColor,
            barColor: this.barColors[0].value,
            operatorCondition: this.valueOperators?.[0].value as FormatOperator,
            value: null,
            rangeFrom: null,
            rangeTo: null,
        };
        this.columnConditions.push(columnCondition);
        const guid = this.columnConditionVizTab[0].vizTabs.length;
        this.addColumnConditionVizTab(guid, true, columnCondition);
        this.columnConditionChange.emit(this.columnConditions);
    }

    protected onOperatorConditionChanged(selectedOperatorCondition: DropdownOption, vizTab: VizTab): void {
        vizTab.selectedOperatorCondition = selectedOperatorCondition;
        vizTab.columnCondition.operatorCondition = selectedOperatorCondition.value as FormatOperator;
        vizTab.showInputRange = showInputRange(vizTab.columnCondition.operatorCondition, this.valueOperators ?? []);
        this.columnConditionChange.emit(this.columnConditions);
    }

    protected onInput(
        event: Event,
        regex: RegExp | string | null,
        columnCondition: CellFormat,
        type: 'rangeFrom' | 'rangeTo' | 'value',
    ): void {
        const input = (event.target as HTMLInputElement).value;
        if (regex && !input.match(regex)) {
            (event.target as HTMLInputElement).value = input.substring(0, input.length - 1);
        }
        columnCondition[type] = (event.target as HTMLInputElement).value !== '' ? (event.target as HTMLInputElement).value : null;
        this.columnConditionChange.emit(this.columnConditions);
    }

    protected onFuzzyDateChanged(selectedFuzzyDate: DropdownOption, columnCondition: CellFormat): void {
        columnCondition.value = selectedFuzzyDate.value as string;
        this.columnConditionChange.emit(this.columnConditions);
    }

    protected onCellFontColorSelected(columnCondition: CellFormat, item: CellColors): void {
        columnCondition.cellColor = item.cellColor;
        columnCondition.fontColor = item.fontColor;
        this.columnConditionChange.emit(this.columnConditions);
    }

    protected onBarConditionChanged(columnCondition: CellFormat, selectedCondition: DropdownOption): void {
        columnCondition.operatorCondition = selectedCondition.value as FormatOperator;
        this.columnConditionChange.emit(this.columnConditions);
    }

    protected onBarColorSelected(columnCondition: CellFormat, barColor: string): void {
        columnCondition.barColor = barColor;
        this.columnConditionChange.emit(this.columnConditions);
    }

    private addColumnConditionVizTab(guid: number, active: boolean, columnCondition: CellFormat): void {
        this.columnConditionVizTab[0].vizTabs.push(
            {
                guid,
                active,
                label: `CN ${guid + 1}`,
                columnCondition,
                selectedOperatorCondition: this.valueOperators?.find((operator) => operator.value === columnCondition.operatorCondition),
                showInputRange: showInputRange(columnCondition.operatorCondition, this.valueOperators ?? []),
            },
        );
    }
}
